import { CreationPanel, CustomDialog } from '@features/common';
import SightLoading from '@features/common/Loading/SightLoading';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import {
	handleProcessNumberKeyPress,
	handleProcessNumberPaste,
} from '@features/processes/components/ProcessEditionForm/ProcessEditionForm.handlers';
import {
	generateActualConsecutiveRules,
	generateFinalConsecutiveRules,
	INITIAL_CONSECUTIVE_RULES,
	PREFIX_RULES,
} from '@features/processes/constants/process-forms.constants';
import {
	ProcessEditionFormType,
	ProcessGuidesRangeFormType,
} from '@features/processes/interfaces/process-edition.interface';
import useRequests from '@hooks/requests.hook';
import { ClienteSuite } from '@interfaces/firestore-documents.interfaces';
import { Autocomplete, Checkbox, Divider, Grid, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import {
	handleBackBeforeCloseDialog,
	handleClosePanel,
	handleSaveGuidesRange,
	handleSaveGuidesRangeAgw,
} from './AddGuidesRangeForm.handlers';
import useAddGuidesRangeFormStyles from './AddGuidesRangeForm.styles';

interface Props {
	clienteGeneraNumeracion?: boolean;
	onClose(): void;
	showActual?: boolean;
	id_proceso?: string;
	InputClienteGenera?: string | React.ReactElement;
	agw?: boolean;
	nit?: string;
	codigoDiv?: string;
	cities: Cities[];
	allData: ClienteSuite | null;
	form?: UseFormReturn<ProcessEditionFormType>;
	getDataById: (identificacion: string) => Promise<void>;
}

const AddGuidesRangeForm: React.FC<Props> = ({
	clienteGeneraNumeracion = true,
	onClose,
	agw = false,
	showActual = true,
	id_proceso,
	// InputClienteGenera,
	nit,
	codigoDiv,
	cities,
	form,
	allData,
	getDataById,
}) => {
	// States
	const [show, setShow] = useState(false);
	const [beforeCloseDialog, setBeforeCloseDialog] = useState<boolean>(false);
	const [clienteGeneraNumeracionCrear, setClienteGeneraNumeracionCrear] =
		useState(clienteGeneraNumeracion);
	const [citie, setCitie] = useState<Cities | null | string>({
		nombre: '',
		codigo: '',
	});

	// Hooks
	const guidesForm = useForm<ProcessGuidesRangeFormType>();
	const { enqueueSnackbar } = useSnackbar();
	const { request, requestBackendSuite } = useRequests();
	const classes = useAddGuidesRangeFormStyles();
	// Variables
	const { control, watch, setValue, setError } = guidesForm;

	const consecutivoDesde = Number(watch('consecutivo_desde')) || 0;
	const consecutivoHasta = Number(watch('consecutivo_hasta')) || 1;
	useEffect(() => {
		const wheel = document?.getElementsByClassName('numberInput');
		for (let index = 0; index < wheel.length; index++) {
			wheel[index].addEventListener(
				'wheel',
				function (e) {
					e.preventDefault();
				},
				{ passive: false }
			);
		}
	}, []);
	useEffect(() => {
		if (allData) {
			setValue('correo', '');
			setValue('telefono', allData.telefono_fijo);
			setValue('nombre', allData.nombre);
			setValue('direccion', allData.direccion_rango);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		if (allData && cities) {
			const filter = cities.filter(
				(i) => String(i.codigo_interno) === String(allData.codigo_municipio)
			);
			setCitie(filter[0]);
			setValue('ciudad', filter[0].codigo);
			setValue('nombre_ciudad', filter[0].nombre);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cities]);
	return (
		<>
			<SightLoading show={show} />
			{beforeCloseDialog && (
				<CustomDialog
					leftAction="CERRAR"
					onLeftAction={onClose}
					onRightAction={handleBackBeforeCloseDialog(setBeforeCloseDialog)}
					rightAction="SEGUIR EDITANDO"
					title="¿Desea cerrar el formulario?"
				/>
			)}
			<CreationPanel
				onClose={handleClosePanel(guidesForm, onClose, setBeforeCloseDialog)}
				onSave={
					agw
						? handleSaveGuidesRangeAgw(
								guidesForm,
								enqueueSnackbar,
								onClose,
								request,
								nit || '',
								codigoDiv || '',
								setShow,
								getDataById
						  )
						: handleSaveGuidesRange(
								guidesForm,
								enqueueSnackbar,
								onClose,
								id_proceso || '',
								request,
								requestBackendSuite,
								clienteGeneraNumeracionCrear,
								form,
								setShow,
								getDataById,
								nit || ''
						  )
				}
				title="Nuevo rango de guías"
				width={714}
			>
				<h3 style={{ marginTop: 0 }} className={classes.h3}>
					Datos del cliente
				</h3>
				<Typography color="#707070" fontFamily="Montserrat" fontSize={14} fontWeight={500}>
					Nombre o Razón social
				</Typography>
				<TextField
					className={classes.textField}
					fullWidth
					error={Boolean(guidesForm.formState.errors.nombre)}
					helperText={guidesForm.formState.errors.nombre?.message}
					multiline
					size="medium"
					minRows={1}
					maxRows={5}
					style={{ marginTop: 8 }}
					type="text"
					{...guidesForm.register('nombre', {
						required: false,
					})}
				/>
				<Grid container spacing={3}>
					<Grid item md={6}>
						<Typography
							style={{ marginTop: 16 }}
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Ciudad
						</Typography>
						<Controller
							control={control}
							name="ciudad"
							rules={{
								required: false,
							}}
							render={({ fieldState }) => (
								<Autocomplete
									options={cities}
									getOptionLabel={(option) => option.nombre}
									autoHighlight={true}
									style={{ marginTop: 8 }}
									value={citie}
									onChange={(_e, v) => {
										setError('ciudad', { message: undefined });
										if (typeof v === 'object') {
											setValue('ciudad', v?.codigo);
											setValue('nombre_ciudad', v?.nombre);
											setCitie(v);
										} else {
											setValue('ciudad', '');
											setValue('nombre_ciudad', '');
											setCitie({
												nombre: '',
												codigo: '',
											});
										}
									}}
									freeSolo
									renderInput={(params) => (
										<TextField
											{...params}
											className={classes.textField}
											size="medium"
											error={Boolean(fieldState.error?.message)}
											helperText={fieldState.error?.message}
										/>
									)}
								/>
							)}
						/>
					</Grid>
					<Grid item md={6}>
						<Typography
							style={{ marginTop: 16 }}
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Dirección
						</Typography>
						<TextField
							fullWidth
							error={Boolean(guidesForm.formState.errors.direccion)}
							helperText={guidesForm.formState.errors.direccion?.message}
							multiline
							size="medium"
							className={classes.textField}
							style={{ marginTop: 8 }}
							type="text"
							{...guidesForm.register('direccion', {
								required: false,
							})}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid item md={6}>
						<Typography
							style={{ marginTop: 16 }}
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Teléfono
						</Typography>
						<TextField
							fullWidth
							error={Boolean(guidesForm.formState.errors.telefono)}
							className={classes.textField}
							helperText={guidesForm.formState.errors.telefono?.message}
							multiline
							size="medium"
							style={{ marginTop: 8 }}
							type="text"
							{...guidesForm.register('telefono', {
								required: false,
							})}
						/>
					</Grid>
					<Grid item md={6}>
						<Typography
							style={{ marginTop: 16 }}
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Correo electrónico
						</Typography>
						<TextField
							fullWidth
							error={Boolean(guidesForm.formState.errors.correo)}
							helperText={guidesForm.formState.errors.correo?.message}
							multiline
							className={classes.textField}
							size="medium"
							style={{ marginTop: 8 }}
							type="text"
							{...guidesForm.register('correo', {
								required: false,
							})}
						/>
					</Grid>
				</Grid>
				<Divider style={{ marginTop: 20 }}></Divider>
				<h3 className={classes.h3}>Rango de guía</h3>
				<Grid container spacing={3}>
					<Grid item md={6}>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Prefijo
						</Typography>
						<Controller
							control={control}
							name="prefijo_consecutivo"
							rules={PREFIX_RULES}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className={`numberInput ${classes.textField}`}
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{ min: 0 }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="0000"
									size="medium"
									style={{ marginTop: 8 }}
									type="text"
									value={value}
								/>
							)}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid item md={6}>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Inicial
						</Typography>
						<Controller
							control={control}
							name="consecutivo_desde"
							rules={INITIAL_CONSECUTIVE_RULES}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className={`numberInput ${classes.textField}`}
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{ min: 0, max: 9999999 }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="medium"
									style={{ marginTop: 8 }}
									type="text"
									value={value}
								/>
							)}
						/>
					</Grid>
					<Grid item md={6}>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Final
						</Typography>
						<Controller
							control={control}
							name="consecutivo_hasta"
							rules={generateFinalConsecutiveRules(consecutivoDesde)}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									error={Boolean(error?.message)}
									className={`numberInput ${classes.textField}`}
									helperText={error?.message}
									inputProps={{ min: consecutivoDesde, max: 9999999 }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="medium"
									style={{ marginTop: 8 }}
									type="text"
									value={value}
								/>
							)}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					{showActual && (
						<Grid item md={6}>
							<>
								<Grid
									container
									alignItems="center"
									justifyContent="space-between"
									style={{ marginTop: 8 }}
								>
									<Grid item>
										<Typography
											color="#707070"
											fontFamily="Montserrat"
											fontSize={14}
											fontWeight={500}
										>
											Cliente genera numeración
										</Typography>
									</Grid>
									<Grid item>
										<Checkbox
											checked={clienteGeneraNumeracionCrear}
											onChange={(e) => {
												setClienteGeneraNumeracionCrear(e.target.checked);
											}}
										/>
									</Grid>
								</Grid>
								<Typography
									color="#707070"
									fontFamily="Montserrat"
									fontSize={14}
									fontWeight={500}
									style={{ marginTop: 8 }}
								>
									Actual
								</Typography>
								<Controller
									control={control}
									name="consecutivo_actual"
									rules={generateActualConsecutiveRules(
										clienteGeneraNumeracionCrear,
										consecutivoDesde,
										consecutivoHasta
									)}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											fullWidth
											disabled={clienteGeneraNumeracionCrear}
											error={Boolean(error?.message)}
											helperText={error?.message}
											inputProps={{
												min: consecutivoDesde,
												max: consecutivoHasta,
											}}
											onChange={onChange}
											className={`numberInput ${classes.textField}`}
											onKeyPress={handleProcessNumberKeyPress}
											onPaste={handleProcessNumberPaste}
											placeholder="9.999.999"
											size="medium"
											style={{ marginTop: 8 }}
											type="text"
											value={value}
										/>
									)}
								/>
							</>
						</Grid>
					)}
				</Grid>
			</CreationPanel>
		</>
	);
};

export default AddGuidesRangeForm;
